import React from 'react';
import { Capability } from './permissions';
import { useCan } from './useCan';

interface Props {
  capability: Capability;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Render `children` only when the current employee has `capability`. When
 * denied, render `fallback` (defaults to `null`). Pair with a read-only banner
 * or a disabled UI placeholder where appropriate — silent hides are fine for
 * action buttons, but list-level denials should usually surface SOMETHING.
 *
 * Phase 1 (custom roles): this now resolves through `useCan`, which consults
 * the acting employee's *live* `/me.capabilities` list when present (falling
 * back to the static role matrix otherwise). That means owner edits to a role's
 * capabilities take effect on the next `/me` with no code change — every
 * existing `<RoleGate>` call site inherits this for free.
 *
 * For inline conditions (or when you need the role value) use the `useCan`
 * hook directly.
 */
export function RoleGate({ capability, fallback = null, children }: Props) {
  const allowed = useCan(capability);
  if (!allowed) return <>{fallback}</>;
  return <>{children}</>;
}
